/**
 * Business details the site shows (contact, support hours, SEO, homepage hero) are edited in
 * Admin → Settings and served by GET /api/site-config.
 *
 * contactInfo.js, seoConfig.js and heroConfig.js hold the defaults. applySiteConfig() writes the live
 * values into those same objects, so their helpers (getWhatsAppUrl, getSupportStatus, …) always read the
 * current values. Components that show them call useSiteConfig() to re-render when the values change.
 *
 * Start-up: the last copy (localStorage) applies immediately, then the API's answer replaces it.
 */
import { useSyncExternalStore } from 'react';
import apiClient from '../api/apiClient';
import contactInfo, { describeHours, describeWeekdays } from './contactInfo';
import seoConfig from './seoConfig';
import heroConfig from '../pages/home/heroConfig';

const CACHE_KEY = 'pk_site_config';
const HERO_VALUES = ['original', '1', '2', '3', '4'];

let version = 0;
const listeners = new Set();

const subscribe = listener => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const getVersion = () => version;

const text = (value, fallback) =>
  typeof value === 'string' && value.trim() ? value.trim() : fallback;
const optional = (value, fallback) =>
  typeof value === 'string' ? value.trim() : fallback;
const hour = (value, fallback) =>
  Number.isInteger(value) && value >= 0 && value <= 24 ? value : fallback;

/** wa.me wants digits with the country code; a bare 10-digit number is taken to be Indian. */
const whatsAppDigits = value => {
  const digits = value.replace(/\D/g, '');
  return digits.length === 10 ? `91${digits}` : digits;
};

/** Applies a /api/site-config payload over the defaults. Unknown or empty values keep the current ones. */
export function applySiteConfig(config) {
  if (!config || typeof config !== 'object') return;

  contactInfo.companyName = text(config.companyName, contactInfo.companyName);
  contactInfo.tagline = optional(config.tagline, contactInfo.tagline);
  contactInfo.owner.name = text(config.ownerName, contactInfo.owner.name);
  contactInfo.owner.website = text(
    config.operatedBy,
    contactInfo.owner.website
  );

  const phone = text(config.phone, contactInfo.phone.display);
  contactInfo.phone.primary = phone;
  contactInfo.phone.display = phone;

  const whatsApp = text(config.whatsApp, contactInfo.whatsapp.display);
  contactInfo.whatsapp.number =
    whatsAppDigits(whatsApp) || contactInfo.whatsapp.number;
  contactInfo.whatsapp.display = whatsApp;

  contactInfo.email.support = optional(
    config.supportEmail,
    contactInfo.email.support
  );
  contactInfo.email.info = optional(config.infoEmail, contactInfo.email.info);

  contactInfo.address.city = text(config.city, contactInfo.address.city);
  contactInfo.address.state = text(config.state, contactInfo.address.state);
  contactInfo.address.country = text(
    config.country,
    contactInfo.address.country
  );

  const hours = contactInfo.businessHours;
  const days = Array.isArray(config.supportDays)
    ? config.supportDays.filter(d => Number.isInteger(d) && d >= 0 && d <= 6)
    : [];
  if (days.length) hours.weekdays = days;
  hours.openHour = hour(config.supportOpenHour, hours.openHour);
  hours.closeHour = hour(config.supportCloseHour, hours.closeHour);
  hours.days = describeWeekdays(hours.weekdays);
  hours.time = describeHours(hours.openHour, hours.closeHour, hours.timezone);

  seoConfig.siteName = text(config.siteName, seoConfig.siteName);
  seoConfig.siteUrl = text(config.siteUrl, seoConfig.siteUrl).replace(
    /\/+$/,
    ''
  );
  seoConfig.defaultTitle = text(config.seoTitle, seoConfig.defaultTitle);
  seoConfig.defaultDescription = text(
    config.seoDescription,
    seoConfig.defaultDescription
  );
  seoConfig.pages.home.title = seoConfig.defaultTitle;
  seoConfig.pages.home.description = seoConfig.defaultDescription;

  const hero = String(config.homeHero ?? '');
  if (HERO_VALUES.includes(hero)) {
    heroConfig.activeHero = hero === 'original' ? hero : Number(hero);
  }

  version += 1;
  listeners.forEach(listener => listener());
}

/**
 * Re-renders the calling component when the site details change. Returns the (live) config objects;
 * the imported contactInfo / seoConfig / heroConfig are the same objects.
 */
export function useSiteConfig() {
  useSyncExternalStore(subscribe, getVersion, getVersion);
  return { contactInfo, seoConfig, heroConfig };
}

const readCache = () => {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
  } catch {
    return null;
  }
};

const writeCache = config => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(config));
  } catch {
    // Private mode or full storage: the defaults show until the API answers next time.
  }
};

/** Fetches the live details and applies them. Safe to call again (e.g. after saving in the admin panel). */
export async function refreshSiteConfig() {
  try {
    const response = await apiClient.get('/api/site-config', { timeout: 8000 });
    const config = response.data?.data;
    if (config) {
      writeCache(config);
      applySiteConfig(config);
    }
  } catch {
    // Keep the cached or default details; the site still works without this call.
  }
}

/** Call once at start-up: applies the cached copy synchronously, then refreshes from the API. */
export function loadSiteConfig() {
  applySiteConfig(readCache());
  return refreshSiteConfig();
}
